import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import PaymentService from '../../services/payment.service';

const PaymentList: React.FC = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // all, payment, expense
  const [sortField, setSortField] = useState<string>('paymentDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchPayments();
  }, [methodFilter, typeFilter, sortField, sortOrder]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: ['invoice', 'invoice.customer', 'receivedBy', 'account'],
        'pagination[pageSize]': 100,
        sort: `${sortField}:${sortOrder}`,
      };

      if (methodFilter !== 'all') {
        params['filters[paymentMethod][$eq]'] = methodFilter;
      }

      if (typeFilter === 'payment') {
        params['filters[isExpense][$eq]'] = false;
      } else if (typeFilter === 'expense') {
        params['filters[isExpense][$eq]'] = true;
      }

      const response = await PaymentService.getAll(params);
      setPayments(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await PaymentService.delete(id);
        fetchPayments();
      } catch (error) {
        console.error('Error deleting record:', error);
      }
    }
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'cash':
        return 'success';
      case 'card':
        return 'primary';
      case 'bankTransfer':
        return 'info';
      case 'check':
        return 'secondary';
      case 'other':
        return 'default';
      default:
        return 'default';
    }
  };

  const getMethodLabel = (method: string) => {
    switch (method) {
      case 'cash':
        return 'Cash';
      case 'card':
        return 'Card';
      case 'bankTransfer':
        return 'Bank Transfer';
      case 'check':
        return 'Check';
      case 'other':
        return 'Other';
      default:
        return method;
    }
  };


  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Filter payments client-side for search (since search is not implemented server-side)
  const filteredPayments = payments.filter((payment) => {
    if (!searchTerm) return true;
    const invoice = payment.attributes?.invoice?.data?.attributes;
    const customer = invoice?.customer?.data?.attributes;
    const searchLower = searchTerm.toLowerCase();
    return (
      payment.attributes?.paymentNumber?.toLowerCase().includes(searchLower) ||
      invoice?.invoiceNumber?.toLowerCase().includes(searchLower) ||
      customer?.companyName?.toLowerCase().includes(searchLower) ||
      customer?.contactPerson?.toLowerCase().includes(searchLower) ||
      payment.attributes?.reference?.toLowerCase().includes(searchLower) ||
      payment.attributes?.description?.toLowerCase().includes(searchLower) ||
      payment.attributes?.category?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Payments & Expenses
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/payments/create')}
        >
          New Payment/Expense
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <TextField
            fullWidth
            label="Search..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by payment #, ref #, customer, description, or reference"
          />
          <TextField
            select
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="payment">Payments</MenuItem>
            <MenuItem value="expense">Expenses</MenuItem>
          </TextField>
          <TextField
            select
            label="Payment Method"
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Methods</MenuItem>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="card">Card</MenuItem>
            <MenuItem value="bankTransfer">Bank Transfer</MenuItem>
            <MenuItem value="check">Check</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
              value={`${sortField}:${sortOrder}`}
              label="Sort By"
              onChange={(e) => {
                const [field, order] = e.target.value.split(':');
                setSortField(field);
                setSortOrder(order as 'asc' | 'desc');
              }}
            >
              <MenuItem value="paymentDate:desc">Date (Newest First)</MenuItem>
              <MenuItem value="paymentDate:asc">Date (Oldest First)</MenuItem>
              <MenuItem value="amount:desc">Amount (High to Low)</MenuItem>
              <MenuItem value="amount:asc">Amount (Low to High)</MenuItem>
              <MenuItem value="paymentNumber:asc">Payment # (A-Z)</MenuItem>
              <MenuItem value="paymentNumber:desc">Payment # (Z-A)</MenuItem>
              <MenuItem value="paymentMethod:asc">Method (A-Z)</MenuItem>
              <MenuItem value="paymentMethod:desc">Method (Z-A)</MenuItem>
              <MenuItem value="createdAt:desc">Created (Newest First)</MenuItem>
              <MenuItem value="createdAt:asc">Created (Oldest First)</MenuItem>
            </Select>
          </FormControl>
          {(searchTerm || methodFilter !== 'all' || typeFilter !== 'all') && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchTerm('');
                setMethodFilter('all');
                setTypeFilter('all');
              }}
              sx={{ minWidth: 120 }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Ref / Description</TableCell>
              <TableCell>Customer / Category</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Method</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredPayments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              filteredPayments.map((payment) => {
                const attrs = payment.attributes;
                const isExpense = attrs?.isExpense || false;
                const invoice = attrs?.invoice?.data?.attributes;
                const customer = invoice?.customer?.data?.attributes;

                return (
                  <TableRow key={payment.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {attrs?.paymentNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={isExpense ? 'Expense' : 'Payment'}
                        color={isExpense ? 'error' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {isExpense ? (
                        <Typography variant="body2" color="text.secondary">
                          {attrs?.description || '-'}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ cursor: invoice ? 'pointer' : 'default', color: invoice ? 'primary.main' : 'text.secondary' }}
                          onClick={() => invoice && navigate(`/invoices/${attrs?.invoice?.data?.id}`)}
                        >
                          {invoice?.invoiceNumber || '-'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isExpense ? (
                        <Chip
                          label={attrs?.category?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || '-'}
                          variant="outlined"
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2">
                          {customer?.companyName || customer?.contactPerson || '-'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(attrs?.paymentDate)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="bold" color={isExpense ? 'error.main' : 'success.main'}>
                        {formatCurrency(attrs?.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getMethodLabel(attrs?.paymentMethod)}
                        color={getMethodColor(attrs?.paymentMethod)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/payments/${payment.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/payments/${payment.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(payment.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PaymentList;

